// src/features/channelManager/pages/AriGridPage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button, Card, Dropdown, message, Select, Space, Spin, Typography, Modal, Tag, Tooltip, Switch } from "antd";
import { DownOutlined, ReloadOutlined, SaveOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { channelsApi } from "./api/channelsApi";
import AriGrid from "./AriGrid";
import BulkUpdateDrawer from "./components/BulkUpdateDrawer";
import ChangesReviewDrawer from "./components/ChangesReviewDrawer";
import RatePlanDrawer from "./components/ari/RatePlanDrawer";
import DistributionCalendarPreview, { getDirectSummary } from "./components/DistributionCalendarPreview";
import DistributionDayPreviewDrawer from "./components/DistributionDayPreviewDrawer";
import DistributionBulkPreviewDrawer from "./components/DistributionBulkPreviewDrawer";
import DistributionTrustCenter from "./components/DistributionTrustCenter";
import "./distributionPreview.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const { Text } = Typography;

const DAYS_OPTIONS = [
  { value: 14, label: "14 days" },
  { value: 21, label: "21 days" },
  { value: 28, label: "28 days" },
];

const VIEW_OPTIONS = [
  { value: "rate_availability", label: "Rate & Availability" },
  { value: "only_availability", label: "Only Availability" },
  { value: "only_rate", label: "Only Rate" },
];

function safeYMD(d) {
  return dayjs(d).format("YYYY-MM-DD");
}

function buildDates(startDate, days) {
  const start = dayjs(startDate).startOf("day");
  return Array.from({ length: days }, (_, i) => start.add(i, "day").format("YYYY-MM-DD"));
}

function calendarRowsFromResponse(resp) {
  const rows = Array.isArray(resp?.data?.data) ? resp.data.data : [];
  const rateOverrides = Array.isArray(resp?.data?.rateOverrides) ? resp.data.rateOverrides : [];

  const rowsByDate = {};
  rows.forEach((row) => {
    const key = String(row?.inventory_date || "").slice(0, 10);
    if (key) rowsByDate[key] = row;
  });

  const overridesByDate = {};
  rateOverrides.forEach((row) => {
    const key = String(row?.inventory_date || "").slice(0, 10);
    if (!key) return;
    if (!overridesByDate[key]) overridesByDate[key] = [];
    overridesByDate[key].push(row);
  });

  return { rowsByDate, overridesByDate };
}

function normalizeDirectRateOptions(raw, currency = "INR") {
  return (Array.isArray(raw) ? raw : [])
    .filter((r) => String(r?.source || "").toUpperCase() === "BE" && r?.status !== false)
    .map((r) => ({
      ...r,
      rate_id: Number(r?.rate_id ?? r?.id),
      occ1_rate:
        r?.occ1_rate == null || r?.occ1_rate === ""
          ? null
          : Number(r.occ1_rate),
      occ2_rate:
        r?.occ2_rate == null || r?.occ2_rate === ""
          ? null
          : Number(r.occ2_rate),
      is_guest_wise_rate: Boolean(r?.is_guest_wise_rate),
      guest_wise_rates:
        r?.guest_wise_rates && typeof r.guest_wise_rates === "object"
          ? Object.fromEntries(
              Object.entries(r.guest_wise_rates).map(([key, value]) => [
                String(key),
                value == null || value === "" ? null : Number(value),
              ])
            )
          : {},
      currency_code: String(r?.currency_code || currency || "INR").toUpperCase(),
    }))
    .filter((r) => Number.isFinite(r.rate_id) && r.rate_id > 0);
}

function mergeDirectRateOverride(existing = {}, patch = {}, currency = "INR") {
  const mergedGuests = { ...(existing?.guest_wise_rates || {}) };
  Object.entries(patch?.guest_wise_rates || {}).forEach(([guestCount, value]) => {
    if (value === null || value === undefined || value === "") return;
    mergedGuests[String(guestCount)] = Number(value);
  });

  const merged = {
    ...existing,
    rate_id: Number(patch?.rate_id ?? existing?.rate_id),
    currency_code: String(
      patch?.currency_code ||
        existing?.currency_code ||
        currency ||
        "INR"
    ).toUpperCase(),
    guest_wise_rates: mergedGuests,
  };

  if (Object.prototype.hasOwnProperty.call(patch || {}, "occ1_rate")) {
    merged.occ1_rate =
      patch.occ1_rate == null || patch.occ1_rate === ""
        ? null
        : Number(patch.occ1_rate);
  }

  if (Object.prototype.hasOwnProperty.call(patch || {}, "occ2_rate")) {
    merged.occ2_rate =
      patch.occ2_rate == null || patch.occ2_rate === ""
        ? null
        : Number(patch.occ2_rate);
  }

  return merged;
}

function buildPreservedBeCalendarItem({
  roomTypeId,
  date,
  availability,
  currency,
  beRead,
  restrictionPatch = {},
  pricingPatch = {},
}) {
  const row = beRead?.rowsByDate?.[date] || {};
  const existingRateOverrides = beRead?.overridesByDate?.[date] || [];
  const has = (key) =>
    Object.prototype.hasOwnProperty.call(restrictionPatch || {}, key);

  const overrideMap = new Map(
    existingRateOverrides
      .map((override) => [
        Number(override?.rate_id),
        {
          rate_id: Number(override?.rate_id),
          occ1_rate:
            override?.occ1_rate == null || override?.occ1_rate === ""
              ? null
              : Number(override.occ1_rate),
          occ2_rate:
            override?.occ2_rate == null || override?.occ2_rate === ""
              ? null
              : Number(override.occ2_rate),
          guest_wise_rates:
            override?.guest_wise_rates &&
            typeof override.guest_wise_rates === "object"
              ? { ...override.guest_wise_rates }
              : {},
          currency_code: String(
            override?.currency_code ||
              row?.currency_code ||
              currency ||
              "INR"
          ).toUpperCase(),
        },
      ])
      .filter(([rateId]) => Number.isFinite(rateId) && rateId > 0)
  );

  (pricingPatch?.rateOverrides || []).forEach((patch) => {
    const rateId = Number(patch?.rate_id);
    if (!Number.isFinite(rateId) || rateId <= 0) return;
    const existing = overrideMap.get(rateId) || {
      rate_id: rateId,
      currency_code: String(
        row?.currency_code || currency || "INR"
      ).toUpperCase(),
      guest_wise_rates: {},
    };
    overrideMap.set(
      rateId,
      mergeDirectRateOverride(existing, patch, currency)
    );
  });

  const hasBaseRate = Object.prototype.hasOwnProperty.call(
    pricingPatch || {},
    "overrideRate"
  );

  return {
    be_room_type_id: Number(roomTypeId),
    inventory_date: date,
    allotment: Math.max(0, Number(availability || 0)),
    stop_sell: has("stopSell")
      ? Boolean(restrictionPatch.stopSell)
      : Boolean(row?.stop_sell ?? row?.stopSell ?? false),
    min_stay: has("minStay")
      ? Math.max(1, Number(restrictionPatch.minStay || 1))
      : Math.max(1, Number(row?.min_stay ?? row?.minStay ?? 1) || 1),
    closed_to_arrival: has("cta")
      ? Boolean(restrictionPatch.cta)
      : Boolean(row?.closed_to_arrival ?? row?.cta ?? false),
    closed_to_departure: has("ctd")
      ? Boolean(restrictionPatch.ctd)
      : Boolean(row?.closed_to_departure ?? row?.ctd ?? false),
    rate_card_id:
      row?.rate_card_id == null || row?.rate_card_id === ""
        ? null
        : Number(row.rate_card_id),
    override_rate: hasBaseRate
      ? pricingPatch.overrideRate == null || pricingPatch.overrideRate === ""
        ? null
        : Number(pricingPatch.overrideRate)
      : row?.override_rate == null || row?.override_rate === ""
        ? null
        : Number(row.override_rate),
    currency_code: String(
      row?.currency_code || currency || "INR"
    ).toUpperCase(),
    rate_overrides: Array.from(overrideMap.values()).filter(
      (override) =>
        Number.isFinite(Number(override?.rate_id)) &&
        Number(override.rate_id) > 0
    ),
    notes: String(row?.notes || "").slice(0, 500),
  };
}

function buildBulkDateKeys(startDate, endDate, weekdays = []) {
  const start = dayjs(startDate).startOf("day");
  const end = dayjs(endDate).startOf("day");
  if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) return [];

  const allowed = new Set((weekdays || []).map(Number));
  const result = [];
  let cursor = start;
  while (cursor.isSame(end, "day") || cursor.isBefore(end, "day")) {
    if (!allowed.size || allowed.has(cursor.day())) result.push(cursor.format("YYYY-MM-DD"));
    cursor = cursor.add(1, "day");
    if (result.length > 1000) break;
  }
  return result;
}

function createDistributionOperationId() {
  const stamp = dayjs().format("YYYYMMDD-HHmmss");
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `ARI-${stamp}-${suffix}`;
}

export default function AriGridPage() {
  const params = useParams();
  const navigate = useNavigate();
  const propertyId = params.propertyId || params.id;

  const [loading, setLoading] = useState(true);
  const [gridLoading, setGridLoading] = useState(false);

  const [channelInfo, setChannelInfo] = useState(null);

  const [startDate, setStartDate] = useState(dayjs().startOf("day"));
  const [days, setDays] = useState(14);

  const [viewMode, setViewMode] = useState("rate_availability");
  const [roomFilter, setRoomFilter] = useState(null);
  const [rateFilter, setRateFilter] = useState(null);

  const [grid, setGrid] = useState(null);
  const [draft, setDraft] = useState({});
  const isDirty = useMemo(() => Object.keys(draft).length > 0, [draft]);

  // Frontend-only Distribution Calendar preview. Advanced ARI remains the live Channex workspace.
  const [workspaceView, setWorkspaceView] = useState("calendar");
  const [calendarRoomId, setCalendarRoomId] = useState(null);
  const [showAdvancedDirectReference, setShowAdvancedDirectReference] = useState(false);
  const [beRoomTypeIds, setBeRoomTypeIds] = useState(() => new Set());
  const [beRoomTypeMetaById, setBeRoomTypeMetaById] = useState({});
  const [beRoomTypesLoadState, setBeRoomTypesLoadState] = useState("idle");
  const [beRatesByRoomRange, setBeRatesByRoomRange] = useState({});
  const [beReadStateByRoomRange, setBeReadStateByRoomRange] = useState({});
  const [previewChanges, setPreviewChanges] = useState({});
  const [previewDay, setPreviewDay] = useState(null);
  const [previewBulkOpen, setPreviewBulkOpen] = useState(false);
  const [distributionSavingKey, setDistributionSavingKey] = useState(null);
  const [distributionHealth, setDistributionHealth] = useState({
    status: "idle",
    message: "Global selling controls + Booking Engine Direct pricing are ready. OTA pricing remains independent.",
    updatedAt: null,
  });
  const [trustCenterOpen, setTrustCenterOpen] = useState(false);
  const [distributionOperation, setDistributionOperation] = useState(null);
  const [distributionRecovery, setDistributionRecovery] = useState(null);
  const [distributionRetrying, setDistributionRetrying] = useState(false);
  const [channexVerifyLoading, setChannexVerifyLoading] = useState(false);
  const [channexVerifyResult, setChannexVerifyResult] = useState(null);
  const [channexVerifyOpen, setChannexVerifyOpen] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
const [ratePlanOpen, setRatePlanOpen] = useState(false);
const [ratePlanMode, setRatePlanMode] = useState("create");
const [editingRatePlan, setEditingRatePlan] = useState(null);
  // For ReactDate "open on click"
  const datePickerRef = useRef(null);

  const currency = useMemo(() => grid?.currency || channelInfo?.settings?.currency || "INR", [grid, channelInfo]);

  const rangeLabel = useMemo(() => {
    const s = dayjs(startDate);
    const e = s.add(days - 1, "day");
    return `${s.format("DD MMM YYYY")} → ${e.format("DD MMM YYYY")}`;
  }, [startDate, days]);

  const subTitleLabel = useMemo(() => {
    // no property UUID shown
    return channelInfo?.settings?.property_name || channelInfo?.settings?.hotel_name || "Inventory Control";
  }, [channelInfo]);

  const channelLabel = useMemo(
    () => channelInfo?.name || channelInfo?.channel_name || channelInfo?.code || "Connected channel",
    [channelInfo]
  );

  const mappingHealth = useMemo(() => {
    const rooms = Array.isArray(grid?.rooms) ? grid.rooms : [];
    const unmappedRooms = rooms
      .filter((room) => !beRoomTypeIds.has(Number(room?.pms_room_type_id)))
      .map((room) => ({
        id: Number(room?.pms_room_type_id),
        name: room?.name || room?.room_name || `Room ${room?.pms_room_type_id}`,
      }));
    return {
      status: beRoomTypesLoadState === "error" ? "error" : beRoomTypesLoadState,
      totalRooms: rooms.length,
      mappedCount: Math.max(0, rooms.length - unmappedRooms.length),
      unmappedCount: unmappedRooms.length,
      unmappedRooms,
    };
  }, [grid, beRoomTypeIds, beRoomTypesLoadState]);

  const beginDistributionOperation = useCallback((label) => {
    const operation = {
      id: createDistributionOperationId(),
      label,
      startedAt: dayjs().format("DD MMM YYYY · HH:mm:ss"),
    };
    setDistributionOperation(operation);
    setDistributionRecovery(null);
    return operation;
  }, []);

  const registerDistributionRecovery = useCallback(({
    operation,
    label,
    succeededTargets = [],
    tasks = [],
  }) => {
    const cleanTasks = (tasks || []).filter(Boolean);
    setDistributionRecovery({
      operationId: operation?.id || createDistributionOperationId(),
      label,
      succeededTargets: [...new Set(succeededTargets.filter(Boolean))],
      tasks: cleanTasks,
      createdAt: dayjs().format("DD MMM YYYY · HH:mm:ss"),
    });
  }, []);

  const openChannelMapping = useCallback(() => {
    const code = channelInfo?.code || channelInfo?.channel_code || "";
    navigate(`/channel-manager/setup${code ? `?channel=${encodeURIComponent(code)}` : ""}`);
  }, [navigate, channelInfo]);

  const loadGrid = useCallback(async () => {
    if (!propertyId) {
      message.error("Missing propertyId in URL.");
      setLoading(false);
      return;
    }

    setGridLoading(true);
    setBeRatesByRoomRange({});
    setBeReadStateByRoomRange({});
    setBeRoomTypesLoadState("loading");
    try {
      // Header info (connected channel row)
      const listResp = await channelsApi.listCompanyChannels?.();
      const rows = listResp?.data?.data || [];
      const match = rows.find((r) => String(r.property_id) === String(propertyId));
      setChannelInfo(match || null);

      const resp = await channelsApi.ariGrid({
        property_id: propertyId,
        start_date: safeYMD(startDate),
        days,
      });

      const payload = resp.data?.data || resp.data || {};
      payload.start_date = payload.start_date || safeYMD(startDate);
      payload.days = payload.days || days;
      payload.currency = payload.currency || currency;
      payload.dates = Array.isArray(payload.dates) && payload.dates.length
  ? payload.dates
  : buildDates(payload.start_date, days);

      setGrid(payload);
      setDraft({});

      // Phase 1 composition model:
      // - Channel Manager inventory + OTA prices come from the exact same /ari/grid used by Advanced ARI.
      // - Booking Engine room types are loaded only to prove an exact room-type ID match.
      // No name-based matching and no direct Channex read are used here.
      try {
        const roomTypeResp = await channelsApi.listPmsRoomTypes?.();
        const rawRoomTypes =
          roomTypeResp?.data?.roomTypes ||
          roomTypeResp?.data?.data ||
          roomTypeResp?.data ||
          [];
        const normalizedRoomTypes = Array.isArray(rawRoomTypes) ? rawRoomTypes : [];
        const ids = new Set(
          normalizedRoomTypes
            .map((rt) => Number(rt?.room_type_id ?? rt?.id))
            .filter((id) => Number.isFinite(id) && id > 0)
        );
        const meta = {};
        normalizedRoomTypes.forEach((rt) => {
          const id = Number(rt?.room_type_id ?? rt?.id);
          if (!Number.isFinite(id) || id <= 0) return;
          const baseSingle = Number(
            rt?.base_rate_single ?? rt?.rate_single ?? rt?.baseRateSingle ?? 0
          );
          const baseDouble = Number(
            rt?.base_rate_double ?? rt?.rate_double ?? rt?.baseRateDouble ?? 0
          );
          meta[String(id)] = {
            room_type_id: id,
            type_name: rt?.type_name || rt?.name || `Room ${id}`,
            base_rate_single:
              Number.isFinite(baseSingle) && baseSingle >= 0 ? baseSingle : null,
            base_rate_double:
              Number.isFinite(baseDouble) && baseDouble >= 0 ? baseDouble : null,
            max_occupancy: Number(rt?.max_occupancy || 0) || null,
            total_rooms: Number(rt?.total_rooms || 0) || 0,
          };
        });
        setBeRoomTypeIds(ids);
        setBeRoomTypeMetaById(meta);
        setBeRoomTypesLoadState("loaded");
      } catch (roomTypeErr) {
        console.warn("Booking Engine room-type list unavailable.", roomTypeErr);
        setBeRoomTypeIds(new Set());
        setBeRoomTypeMetaById({});
        setBeRoomTypesLoadState("error");
      }
    } catch (e) {
      console.error(e);
      message.error(e.response?.data?.message || "Failed to load grid");
      setGrid(null);
    } finally {
      setGridLoading(false);
      setLoading(false);
    }
  }, [propertyId, startDate, days, currency]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  useEffect(() => {
    const rooms = grid?.rooms || [];
    if (!rooms.length) return;
    const exists = rooms.some((r) => String(r.pms_room_type_id) === String(calendarRoomId));
    if (!calendarRoomId || !exists) setCalendarRoomId(rooms[0].pms_room_type_id);
  }, [grid, calendarRoomId]);

  useEffect(() => {
    if (
      workspaceView === "advanced" &&
      showAdvancedDirectReference &&
      roomFilter
    ) {
      setCalendarRoomId(Number(roomFilter));
    }
  }, [workspaceView, showAdvancedDirectReference, roomFilter]);

  useEffect(() => {
    if (!calendarRoomId) return;

    const start = safeYMD(startDate);
    const end = dayjs(startDate).add(days - 1, "day").format("YYYY-MM-DD");
    const cacheKey = `${calendarRoomId}|${start}|${end}`;

    if (beRoomTypesLoadState === "idle" || beRoomTypesLoadState === "loading") return;
    if (beRatesByRoomRange[cacheKey] !== undefined) return;

    if (beRoomTypesLoadState === "error") {
      setBeRatesByRoomRange((current) => ({ ...current, [cacheKey]: [] }));
      setBeReadStateByRoomRange((current) => ({
        ...current,
        [cacheKey]: {
          status: "error",
          message: "Booking Engine room types could not be loaded, so Direct pricing cannot be resolved safely."
        },
      }));
      return;
    }

    // Require an exact shared NetShine room-type ID. Never silently match names.
    if (!beRoomTypeIds.has(Number(calendarRoomId))) {
      setBeRatesByRoomRange((current) => ({ ...current, [cacheKey]: [] }));
      setBeReadStateByRoomRange((current) => ({
        ...current,
        [cacheKey]: {
          status: "unmapped",
          message: "No exact Booking Engine room-type ID match was found for this Channel Manager room."
        },
      }));
      return;
    }

    let active = true;
    setBeReadStateByRoomRange((current) => ({
      ...current,
      [cacheKey]: { status: "loading", message: null },
    }));

    Promise.all([
      channelsApi.bookingEngineRateOptions?.(calendarRoomId),
      channelsApi.bookingEngineManualCalendar?.({
        roomTypeId: calendarRoomId,
        startDate: start,
        endDate: end,
      }),
    ])
      .then(([rateResp, calendarResp]) => {
        if (!active) return;

        const rawRates = rateResp?.data?.data || [];
        const masterRates = normalizeDirectRateOptions(rawRates, currency);

        const beCalendarRead = calendarRowsFromResponse(calendarResp);
        const overrideRows = Array.isArray(calendarResp?.data?.rateOverrides)
          ? calendarResp.data.rateOverrides
          : [];

        const overridesByDateAndRate = {};
        overrideRows.forEach((row) => {
          const dateKey = String(row?.inventory_date || "").slice(0, 10);
          const rateId = String(Number(row?.rate_id));
          if (!dateKey || !rateId || rateId === "NaN") return;
          if (!overridesByDateAndRate[dateKey]) overridesByDateAndRate[dateKey] = {};
          overridesByDateAndRate[dateKey][rateId] = row;
        });

        const dates = buildDates(start, days);
        const hydrated = masterRates.map((rate) => {
          const rateId = String(rate.rate_id);
          const dateRates = {};

          dates.forEach((dateKey) => {
            const override = overridesByDateAndRate?.[dateKey]?.[rateId];
            if (!override) return;

            const overrideGuests =
              override?.guest_wise_rates && typeof override.guest_wise_rates === "object"
                ? override.guest_wise_rates
                : {};

            dateRates[dateKey] = {
              ...rate,
              occ1_rate:
                override?.occ1_rate == null || override?.occ1_rate === ""
                  ? rate.occ1_rate
                  : Number(override.occ1_rate),
              occ2_rate:
                override?.occ2_rate == null || override?.occ2_rate === ""
                  ? rate.occ2_rate
                  : Number(override.occ2_rate),
              guest_wise_rates: {
                ...(rate.guest_wise_rates || {}),
                ...overrideGuests,
              },
              currency_code: override?.currency_code || rate?.currency_code || currency,
              source: "date_override",
            };
          });

          return { ...rate, rates: dateRates };
        });

        setBeRatesByRoomRange((current) => ({ ...current, [cacheKey]: hydrated }));
        setBeReadStateByRoomRange((current) => ({
          ...current,
          [cacheKey]: {
            status: "live",
            message: hydrated.length
              ? "Booking Engine master rates and date overrides loaded."
              : "No active Booking Engine rate options are configured for this room.",
            rowsByDate: beCalendarRead.rowsByDate,
            overridesByDate: beCalendarRead.overridesByDate,
            roomTypeDefaults:
              beRoomTypeMetaById[String(calendarRoomId)] || null,
          },
        }));
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Booking Engine Direct pricing read failed.", err);
        setBeRatesByRoomRange((current) => ({ ...current, [cacheKey]: [] }));
        setBeReadStateByRoomRange((current) => ({
          ...current,
          [cacheKey]: {
            status: "error",
            message:
              err?.response?.data?.message ||
              "Booking Engine Direct pricing could not be loaded.",
          },
        }));
      });

    return () => {
      active = false;
    };
  }, [
    workspaceView,
    calendarRoomId,
    startDate,
    days,
    currency,
    beRoomTypeIds,
    beRoomTypeMetaById,
    beRoomTypesLoadState,
    beRatesByRoomRange,
  ]);

  const goToday = () => setStartDate(dayjs().startOf("day"));
  const goPrev = () => setStartDate((d) => dayjs(d).subtract(days, "day"));
  const goNext = () => setStartDate((d) => dayjs(d).add(days, "day"));

  const openCreateRatePlan = () => {
  setEditingRatePlan(null);
  setRatePlanMode("create");
  setRatePlanOpen(true);
};

const openEditRatePlan = (ratePlan) => {
  setEditingRatePlan(ratePlan || null);
  setRatePlanMode("edit");
  setRatePlanOpen(true);
};

const closeRatePlanDrawer = () => {
  setRatePlanOpen(false);
  setEditingRatePlan(null);
};

const handleRatePlanSaved = async () => {
  closeRatePlanDrawer();
  await loadGrid();
};
  const confirmReset = () => {
    if (!isDirty) return;
    Modal.confirm({
      title: "Discard unsaved changes?",
      content: "Your local Advanced ARI edits will be removed and server values restored.",
      okText: "Discard changes",
      cancelText: "Keep editing",
      centered: true,
      width: 440,
      className: "cm-modern-modal cm-confirm-modal",
      okButtonProps: { danger: true, className: "cm-danger-action" },
      cancelButtonProps: { className: "cm-secondary-action" },
      onOk: () => setDraft({}),
    });
  };

  async function saveChanges() {
    if (!propertyId || !isDirty) return;

    const availability = [];
    const rates = [];

    for (const [k, v] of Object.entries(draft)) {
      const parts = k.split("|");

      // A|<pms_room_type_id>|<YYYY-MM-DD>
      if (parts[0] === "A") {
        availability.push({
          pms_room_type_id: Number(parts[1]),
          date: parts[2],
          availability: Number(v),
        });
      }

      // R|<rate_plan_id>|<occ>|<YYYY-MM-DD>
      if (parts[0] === "R") {
        rates.push({
          rate_plan_id: parts[1],
          occupancy: Number(parts[2]),
          date: parts[3],
          amount: Number(v),
        });
      }
    }

    setGridLoading(true);
    try {
      await channelsApi.ariSave({
        property_id: propertyId,
        currency,
        push_to_channex: true,
        changes: { availability, rates },
      });

      message.success(`Saved ${availability.length + rates.length} changes.`);
      await loadGrid();
    } catch (e) {
      console.error(e);
      message.error(e.response?.data?.message || "Save failed");
      throw e;
    } finally {
      setGridLoading(false);
    }
  }

const actionItems = useMemo(() => {
  const items = [
    { key: "bulk", label: "Bulk Update" },
    { key: "create_rate_plan", label: "Create Rate Plan" },
  ];

  if (channelInfo?.code) {
    items.push({
      key: "settings",
      label: "Channel Settings",
      icon: <SettingOutlined />,
    });
  }

  return items;
}, [channelInfo]);

const handleActionsMenuClick = useCallback(
  ({ key }) => {
    if (key === "bulk") {
      if (workspaceView === "calendar") setPreviewBulkOpen(true);
      else setBulkOpen(true);
      return;
    }

    if (key === "create_rate_plan") {
      openCreateRatePlan();
      return;
    }

    if (key === "settings" && channelInfo?.code) {
      navigate(`/channel-manager/settings/${channelInfo.code}`);
    }
  },
  [channelInfo, navigate, workspaceView]
);
  const roomsForFilters = useMemo(() => {
    const rooms = grid?.rooms || [];
    return rooms.map((r) => ({ value: r.pms_room_type_id, label: r.name }));
  }, [grid]);

  const ratesForFilters = useMemo(() => {
    const rooms = grid?.rooms || [];
    const all = [];
    for (const r of rooms) {
      for (const rp of r.ratePlans || []) all.push({ value: rp.id, label: rp.title });
    }
    const seen = new Set();
    return all.filter((x) => (seen.has(x.value) ? false : (seen.add(x.value), true)));
  }, [grid]);

  const calendarData = grid;

  const calendarStart = safeYMD(startDate);
  const calendarEnd = dayjs(startDate).add(days - 1, "day").format("YYYY-MM-DD");
  const calendarBeCacheKey = calendarRoomId
    ? `${calendarRoomId}|${calendarStart}|${calendarEnd}`
    : null;
  const calendarBeRates =
    (calendarBeCacheKey && beRatesByRoomRange[calendarBeCacheKey]) || [];
  const calendarBeReadState =
    (calendarBeCacheKey && beReadStateByRoomRange[calendarBeCacheKey]) || {
      status: "idle",
      message: null,
    };


  const advancedDirectRoom = useMemo(
    () =>
      roomFilter
        ? (grid?.rooms || []).find(
            (room) =>
              String(room?.pms_room_type_id) === String(roomFilter)
          ) || null
        : null,
    [grid, roomFilter]
  );

  const advancedDirectReference = useMemo(() => {
    if (
      !showAdvancedDirectReference ||
      !advancedDirectRoom ||
      calendarBeReadState?.status !== "live"
    ) {
      return [];
    }

    return (grid?.dates || []).map((date) => ({
      date,
      summary: getDirectSummary(
        calendarBeRates,
        date,
        calendarBeReadState
      ),
    }));
  }, [
    showAdvancedDirectReference,
    advancedDirectRoom,
    calendarBeRates,
    calendarBeReadState,
    grid,
  ]);

  const activePreviewKey = previewDay?.room && previewDay?.date
    ? `${previewDay.room.pms_room_type_id}|${previewDay.date}`
    : null;

  const saveDaySellingStatusEverywhere = async ({
    roomId,
    date,
    availability,
    restrictions = {},
  }) => {
    const numericRoomId = Number(roomId);
    const hasAvailability =
      availability !== null &&
      availability !== undefined &&
      Number.isFinite(Number(availability));
    const numericAvailability = hasAvailability
      ? Math.max(0, Number(availability))
      : null;
    const restrictionKeys = Object.keys(restrictions || {});
    const saveKey = `day|${numericRoomId}|${date}`;

    if (!propertyId || !numericRoomId || !date) {
      message.error("Room, date and Channel Manager property are required.");
      return { success: false };
    }

    if (!hasAvailability && !restrictionKeys.length) {
      message.info("Nothing changed.");
      return { success: false };
    }

    if (!beRoomTypeIds.has(numericRoomId)) {
      message.error("This room does not have an exact Booking Engine room-type ID match, so a global selling-rule save is blocked.");
      setTrustCenterOpen(true);
      return { success: false, code: "BE_ROOM_UNMAPPED" };
    }

    const operation = beginDistributionOperation(
      `Selling status · ${date} · room ${numericRoomId}`
    );
    setDistributionSavingKey(saveKey);
    setDistributionHealth({
      status: "saving",
      message: "Saving inventory and selling rules to Booking Engine + Channel Manager…",
      updatedAt: null,
    });

    try {
      // Preflight Booking Engine first so existing values can be preserved.
      const beResp = await channelsApi.bookingEngineManualCalendar({
        roomTypeId: numericRoomId,
        startDate: date,
        endDate: date,
      });
      const beRead = calendarRowsFromResponse(beResp);
      const existingRow = beRead?.rowsByDate?.[date] || null;

      let targetAvailability = numericAvailability;
      if (targetAvailability === null) {
        const current = existingRow?.allotment;
        if (current === null || current === undefined || current === "") {
          message.error("Availability is not initialized for this date. Set available rooms before saving global selling rules.");
          return { success: false, code: "AVAILABILITY_NOT_INITIALIZED" };
        }
        targetAvailability = Math.max(0, Number(current));
      }

      const bePatch = {};
      if (Object.prototype.hasOwnProperty.call(restrictions, "stopSell")) bePatch.stopSell = Boolean(restrictions.stopSell);
      if (Object.prototype.hasOwnProperty.call(restrictions, "minStay")) bePatch.minStay = Math.max(1, Number(restrictions.minStay || 1));
      if (Object.prototype.hasOwnProperty.call(restrictions, "cta")) bePatch.cta = Boolean(restrictions.cta);
      if (Object.prototype.hasOwnProperty.call(restrictions, "ctd")) bePatch.ctd = Boolean(restrictions.ctd);

      const beItem = buildPreservedBeCalendarItem({
        roomTypeId: numericRoomId,
        date,
        availability: targetAvailability,
        currency,
        beRead,
        restrictionPatch: bePatch,
      });

      const cmRestriction = {
        pms_room_type_id: numericRoomId,
        date,
      };
      if (Object.prototype.hasOwnProperty.call(restrictions, "stopSell")) cmRestriction.stop_sell = Boolean(restrictions.stopSell);
      if (Object.prototype.hasOwnProperty.call(restrictions, "minStay")) cmRestriction.min_stay = Math.max(1, Number(restrictions.minStay || 1));
      if (Object.prototype.hasOwnProperty.call(restrictions, "cta")) cmRestriction.closed_to_arrival = Boolean(restrictions.cta);
      if (Object.prototype.hasOwnProperty.call(restrictions, "ctd")) cmRestriction.closed_to_departure = Boolean(restrictions.ctd);

      // Existing NetShine Channel Manager endpoint remains the only OTA/Channex write path.
      await channelsApi.ariSave({
        property_id: propertyId,
        currency,
        push_to_channex: true,
        changes: {
          availability: hasAvailability
            ? [{
                pms_room_type_id: numericRoomId,
                date,
                availability: targetAvailability,
              }]
            : [],
          rates: [],
          restrictions: restrictionKeys.length ? [cmRestriction] : [],
        },
      });

      try {
        await channelsApi.bookingEngineSaveManualCalendar({ items: [beItem] });
      } catch (beWriteError) {
        console.error("Booking Engine selling-status write failed after Channel Manager save.", beWriteError);
        setDistributionHealth({
          status: "partial",
          message: "Channel Manager + Channex desired state saved, but Booking Engine sync needs attention.",
          updatedAt: dayjs().format("HH:mm"),
        });
        registerDistributionRecovery({
          operation,
          label: `Complete Booking Engine sync for ${date}`,
          succeededTargets: ["Channel Manager / Channex"],
          tasks: [
            {
              id: `be-day-${numericRoomId}-${date}`,
              target: "Booking Engine",
              kind: "beSave",
              payload: { items: [beItem] },
            },
          ],
        });
        message.warning("Channel Manager saved the change, but Booking Engine update failed. Retry only the failed Booking Engine step from Distribution Trust.");
        await loadGrid();
        return { success: false, partial: true };
      }

      setPreviewChanges((current) => {
        if (!activePreviewKey || current[activePreviewKey] === undefined) return current;
        const next = { ...current };
        delete next[activePreviewKey];
        return next;
      });

      setDistributionHealth({
        status: "synced",
        message: "Availability and selling rules saved to Booking Engine + Channel Manager. Existing Channex ARI push was requested.",
        updatedAt: dayjs().format("HH:mm"),
      });
      setDistributionRecovery(null);

      message.success(
        restrictionKeys.length
          ? "Selling status saved everywhere."
          : "Availability saved everywhere."
      );
      await loadGrid();
      return { success: true };
    } catch (error) {
      console.error("Global selling-status save failed.", error);
      setDistributionHealth({
        status: "error",
        message: error?.response?.data?.message || "Selling-status save failed.",
        updatedAt: dayjs().format("HH:mm"),
      });
      registerDistributionRecovery({
        operation,
        label: `Retry selling status for ${date}`,
        tasks: [
          {
            id: `selling-${numericRoomId}-${date}`,
            target: "Distribution write",
            kind: "globalSelling",
            payload: { roomId: numericRoomId, date, availability, restrictions },
          },
        ],
      });
      message.error(error?.response?.data?.message || "Selling-status save failed.");
      return { success: false };
    } finally {
      setDistributionSavingKey(null);
    }
  };

  const saveDayDirectPricing = async ({
    roomId,
    date,
    baseRate,
    rateOverrides = [],
    quiet = false,
    skipReload = false,
  }) => {
    const numericRoomId = Number(roomId);
    const saveKey = `direct|${numericRoomId}|${date}`;

    if (!numericRoomId || !date) {
      message.error("A valid Booking Engine room and date are required.");
      return { success: false };
    }

    const hasBaseRate =
      baseRate !== undefined &&
      baseRate !== null &&
      baseRate !== "";

    if (
      hasBaseRate &&
      (!Number.isFinite(Number(baseRate)) || Number(baseRate) < 0)
    ) {
      message.error("Direct date rate must be a valid non-negative amount.");
      return { success: false };
    }

    if (!hasBaseRate && !rateOverrides.length) {
      message.info("No Direct pricing changes to save.");
      return { success: false };
    }

    if (!beRoomTypeIds.has(numericRoomId)) {
      message.error(
        "This room does not have an exact Booking Engine room-type ID match, so Direct pricing cannot be saved safely."
      );
      setTrustCenterOpen(true);
      return { success: false, code: "BE_ROOM_UNMAPPED" };
    }

    const operation = quiet
      ? null
      : beginDistributionOperation(`Direct pricing · ${date} · room ${numericRoomId}`);

    const selectedRoom = (grid?.rooms || []).find(
      (room) => Number(room?.pms_room_type_id) === numericRoomId
    );

    setDistributionSavingKey(saveKey);
    try {
      const beResp = await channelsApi.bookingEngineManualCalendar({
        roomTypeId: numericRoomId,
        startDate: date,
        endDate: date,
      });
      const beRead = calendarRowsFromResponse(beResp);
      const existingRow = beRead?.rowsByDate?.[date] || null;

      const presence = selectedRoom?.availability_source?.[date];
      const cmAvailability = Number(selectedRoom?.availability?.[date]);
      const fallbackAllotment =
        existingRow?.allotment !== null &&
        existingRow?.allotment !== undefined &&
        existingRow?.allotment !== ""
          ? Number(existingRow.allotment)
          : presence === "stored" ||
              presence === "channel_manager" ||
              (Number.isFinite(cmAvailability) && cmAvailability > 0)
            ? Math.max(0, cmAvailability)
            : Math.max(0, Number(selectedRoom?.total_rooms || 0));

      const pricingPatch = {
        rateOverrides: (rateOverrides || []).map((rate) => ({
          ...rate,
          rate_id: Number(rate?.rate_id),
          currency_code: String(
            rate?.currency_code || currency || "INR"
          ).toUpperCase(),
        })),
      };

      if (hasBaseRate) {
        pricingPatch.overrideRate = Number(baseRate);
      }

      const beItem = buildPreservedBeCalendarItem({
        roomTypeId: numericRoomId,
        date,
        availability: fallbackAllotment,
        currency,
        beRead,
        pricingPatch,
      });

      await channelsApi.bookingEngineSaveManualCalendar({ items: [beItem] });

      setDistributionHealth({
        status: "synced",
        message: "Booking Engine Direct pricing updated. OTA pricing was not changed.",
        updatedAt: dayjs().format("HH:mm"),
      });
      if (!quiet) setDistributionRecovery(null);

      if (!quiet) {
        message.success(
          rateOverrides.length
            ? "Booking Engine date pricing saved."
            : "Direct Booking Engine date rate saved."
        );
      }
      if (!skipReload) await loadGrid();
      return { success: true };
    } catch (error) {
      console.error("Booking Engine Direct pricing save failed.", error);
      setDistributionHealth({
        status: "error",
        message:
          error?.response?.data?.message ||
          "Booking Engine Direct pricing save failed.",
        updatedAt: dayjs().format("HH:mm"),
      });
      if (!quiet) {
        registerDistributionRecovery({
          operation,
          label: `Retry Direct pricing for ${date}`,
          tasks: [
            {
              id: `direct-${numericRoomId}-${date}`,
              target: "Booking Engine Direct pricing",
              kind: "directPricing",
              payload: { roomId: numericRoomId, date, baseRate, rateOverrides },
            },
          ],
        });
        message.error(
          error?.response?.data?.message ||
            "Booking Engine Direct pricing save failed."
        );
      }
      return { success: false, error };
    } finally {
      setDistributionSavingKey(null);
    }
  };


  const saveDayOtaPricing = async ({
    roomId,
    date,
    rates = [],
    quiet = false,
    skipReload = false,
  }) => {
    const numericRoomId = Number(roomId);
    const saveKey = `ota|${numericRoomId}|${date}`;

    if (!propertyId || !numericRoomId || !date) {
      if (!quiet) message.error("Room, date and Channel Manager property are required.");
      return { success: false };
    }

    const cleanRates = (Array.isArray(rates) ? rates : [])
      .map((rate) => ({
        rate_plan_id: String(rate?.rate_plan_id || ""),
        occupancy: Math.max(1, Number(rate?.occupancy) || 1),
        date,
        amount: Number(rate?.amount),
      }))
      .filter(
        (rate) =>
          rate.rate_plan_id &&
          Number.isFinite(rate.amount) &&
          rate.amount >= 0
      );

    if (!cleanRates.length) {
      if (!quiet) message.info("No OTA pricing changes to save.");
      return { success: false, code: "NO_OTA_RATE_CHANGES" };
    }

    const operation = quiet
      ? null
      : beginDistributionOperation(`OTA pricing · ${date} · room ${numericRoomId}`);

    setDistributionSavingKey(saveKey);
    try {
      await channelsApi.ariSave({
        property_id: propertyId,
        currency,
        push_to_channex: true,
        changes: {
          availability: [],
          rates: cleanRates,
          restrictions: [],
        },
      });

      setDistributionHealth({
        status: "synced",
        message: "OTA date pricing saved to Channel Manager and pushed through the existing Channex ARI path.",
        updatedAt: dayjs().format("HH:mm"),
      });
      if (!quiet) setDistributionRecovery(null);

      if (!quiet) message.success("OTA pricing saved.");
      if (!skipReload) await loadGrid();
      return { success: true };
    } catch (error) {
      console.error("OTA pricing save failed.", error);
      setDistributionHealth({
        status: "error",
        message:
          error?.response?.data?.message ||
          "OTA pricing save failed.",
        updatedAt: dayjs().format("HH:mm"),
      });
      if (!quiet) {
        registerDistributionRecovery({
          operation,
          label: `Retry OTA pricing for ${date}`,
          tasks: [
            {
              id: `ota-${numericRoomId}-${date}`,
              target: "OTA / Channex pricing",
              kind: "otaPricing",
              payload: { roomId: numericRoomId, date, rates: cleanRates },
            },
          ],
        });
        message.error(
          error?.response?.data?.message ||
            "OTA pricing save failed."
        );
      }
      return { success: false, error };
    } finally {
      setDistributionSavingKey(null);
    }
  };

  const saveDayCommercialPricing = async ({
    roomId,
    date,
    directPricing = null,
    otaRates = [],
  }) => {
    const hasDirect =
      Boolean(directPricing) &&
      (
        (Object.prototype.hasOwnProperty.call(directPricing, "baseRate") &&
          directPricing.baseRate !== null &&
          directPricing.baseRate !== undefined &&
          directPricing.baseRate !== "") ||
        (Array.isArray(directPricing.rateOverrides) &&
          directPricing.rateOverrides.length > 0)
      );
    const hasOta = Array.isArray(otaRates) && otaRates.length > 0;

    if (!hasDirect && !hasOta) {
      message.info("No pricing changes to save.");
      return { success: false };
    }

    const operation = beginDistributionOperation(
      `${hasDirect && hasOta ? "Direct + OTA" : hasDirect ? "Direct" : "OTA"} pricing · ${date} · room ${Number(roomId)}`
    );
    const combinedKey = `pricing|${Number(roomId)}|${date}`;
    setDistributionSavingKey(combinedKey);
    setDistributionHealth({
      status: "saving",
      message:
        hasDirect && hasOta
          ? "Saving Direct + OTA pricing from one commercial action…"
          : hasDirect
            ? "Saving Direct pricing…"
            : "Saving OTA pricing…",
      updatedAt: null,
    });

    let directResult = { success: true, skipped: true };
    let otaResult = { success: true, skipped: true };

    try {
      if (hasDirect) {
        directResult = await saveDayDirectPricing({
          roomId,
          date,
          ...directPricing,
          quiet: true,
          skipReload: true,
        });
      }

      if (hasOta) {
        otaResult = await saveDayOtaPricing({
          roomId,
          date,
          rates: otaRates,
          quiet: true,
          skipReload: true,
        });
      }

      await loadGrid();

      const directOk = !hasDirect || directResult?.success;
      const otaOk = !hasOta || otaResult?.success;

      if (directOk && otaOk) {
        setDistributionHealth({
          status: "synced",
          message:
            hasDirect && hasOta
              ? "Direct + OTA pricing saved from one action. Pricing engines remain independent underneath."
              : hasDirect
                ? "Direct pricing saved."
                : "OTA pricing saved.",
          updatedAt: dayjs().format("HH:mm"),
        });
        setDistributionRecovery(null);
        message.success(
          hasDirect && hasOta
            ? "Direct + OTA pricing saved."
            : hasDirect
              ? "Direct pricing saved."
              : "OTA pricing saved."
        );
        return { success: true };
      }

      setDistributionHealth({
        status: "partial",
        message:
          directOk && !otaOk
            ? "Direct pricing saved, but OTA pricing needs attention."
            : !directOk && otaOk
              ? "OTA pricing saved, but Booking Engine Direct pricing needs attention."
              : "Direct and OTA pricing both need attention.",
        updatedAt: dayjs().format("HH:mm"),
      });
      registerDistributionRecovery({
        operation,
        label: `Complete pricing distribution for ${date}`,
        succeededTargets: [
          directOk && hasDirect ? "Booking Engine Direct pricing" : null,
          otaOk && hasOta ? "OTA / Channex pricing" : null,
        ],
        tasks: [
          !directOk && hasDirect
            ? {
                id: `direct-${Number(roomId)}-${date}`,
                target: "Booking Engine Direct pricing",
                kind: "directPricing",
                payload: { roomId: Number(roomId), date, ...directPricing },
              }
            : null,
          !otaOk && hasOta
            ? {
                id: `ota-${Number(roomId)}-${date}`,
                target: "OTA / Channex pricing",
                kind: "otaPricing",
                payload: { roomId: Number(roomId), date, rates: otaRates },
              }
            : null,
        ],
      });
      message.warning(
        directOk && !otaOk
          ? "Direct pricing saved, but OTA pricing failed."
          : !directOk && otaOk
            ? "OTA pricing saved, but Direct pricing failed."
            : "Pricing save failed."
      );
      return { success: false, partial: directOk !== otaOk };
    } finally {
      setDistributionSavingKey(null);
    }
  };

  const loadBulkDirectPricing = useCallback(
    async ({ roomId }) => {
      const numericRoomId = Number(roomId);

      if (!numericRoomId || !beRoomTypeIds.has(numericRoomId)) {
        return {
          status: "unmapped",
          message:
            "No exact Booking Engine room-type ID match exists for this room.",
          rates: [],
        };
      }

      try {
        const response = await channelsApi.bookingEngineRateOptions(numericRoomId);
        const rates = normalizeDirectRateOptions(
          response?.data?.data || [],
          currency
        );
        return {
          status: "live",
          rates,
          currency,
          roomTypeDefaults:
            beRoomTypeMetaById[String(numericRoomId)] || null,
          message: rates.length
            ? `${rates.length} active Booking Engine rate plan(s) loaded.`
            : "No active Booking Engine rate plans. Room Type price is the Direct default.",
        };
      } catch (error) {
        console.error("Bulk Direct pricing load failed.", error);
        return {
          status: "error",
          rates: [],
          message:
            error?.response?.data?.message ||
            "Booking Engine Direct pricing could not be loaded.",
        };
      }
    },
    [beRoomTypeIds, beRoomTypeMetaById, currency]
  );



  const saveBulkCommercialUpdate = async ({
    roomIds = [],
    startDate: bulkStart,
    endDate: bulkEnd,
    weekdays = [],
    globalUpdates = {},
    directPricing = null,
    otaPricing = null,
  }) => {
    const normalizedRoomIds = [
      ...new Set(
        roomIds
          .map(Number)
          .filter((id) => Number.isFinite(id) && id > 0)
      ),
    ];
    const dateKeys = buildBulkDateKeys(bulkStart, bulkEnd, weekdays);
    const globalKeys = Object.keys(globalUpdates || {});
    const directRateOverrides = directPricing?.rateOverrides || [];
    const hasDirectBaseRate =
      directPricing &&
      Object.prototype.hasOwnProperty.call(directPricing, "baseRate") &&
      directPricing.baseRate !== null &&
      directPricing.baseRate !== undefined &&
      directPricing.baseRate !== "";
    const hasDirectPricing =
      Boolean(directPricing) &&
      (hasDirectBaseRate || directRateOverrides.length > 0);
    const otaRateTemplates = Array.isArray(otaPricing?.rates)
      ? otaPricing.rates
      : [];
    const hasOtaPricing = otaRateTemplates.length > 0;

    if (!normalizedRoomIds.length) {
      message.error("Select at least one room type.");
      return { success: false };
    }
    if (!dateKeys.length) {
      message.error("No dates match the selected range and weekdays.");
      return { success: false };
    }
    if (dateKeys.length > 180) {
      message.error(
        "For safety, bulk updates are limited to 180 matching dates at a time."
      );
      return { success: false };
    }
    if (!globalKeys.length && !hasDirectPricing && !hasOtaPricing) {
      message.error("Select at least one global, Direct or OTA pricing field.");
      return { success: false };
    }

    if ((hasDirectPricing || hasOtaPricing) && normalizedRoomIds.length !== 1) {
      message.error(
        "Direct and OTA rate plans are room-specific. Select exactly one room type when applying pricing."
      );
      return { success: false, code: "PRICING_SINGLE_ROOM_ONLY" };
    }

    const roomsNeedingBeWrite = globalKeys.length
      ? normalizedRoomIds
      : hasDirectPricing
        ? [normalizedRoomIds[0]]
        : [];

    const unmapped = roomsNeedingBeWrite.filter(
      (id) => !beRoomTypeIds.has(id)
    );
    if (unmapped.length) {
      message.error(
        "One or more selected rooms do not have an exact Booking Engine room-type ID match. Save is blocked."
      );
      setTrustCenterOpen(true);
      return { success: false, code: "BE_ROOM_UNMAPPED" };
    }

    const operation = beginDistributionOperation(
      `Bulk distribution · ${dateKeys[0]} → ${dateKeys[dateKeys.length - 1]}`
    );
    setDistributionSavingKey("bulk");
    setDistributionHealth({
      status: "saving",
      message:
        hasDirectPricing && hasOtaPricing
          ? "Saving global controls + Direct + OTA pricing…"
          : hasDirectPricing
            ? "Saving global controls and Booking Engine Direct pricing…"
            : hasOtaPricing
              ? "Saving OTA pricing…"
              : "Saving global availability and selling rules everywhere…",
      updatedAt: null,
    });

    try {
      const roomReads = await Promise.all(
        roomsNeedingBeWrite.map(async (roomTypeId) => {
          const resp = await channelsApi.bookingEngineManualCalendar({
            roomTypeId,
            startDate: dateKeys[0],
            endDate: dateKeys[dateKeys.length - 1],
          });
          return [roomTypeId, calendarRowsFromResponse(resp)];
        })
      );
      const beReads = new Map(roomReads);
      const beItems = [];

      for (const roomTypeId of roomsNeedingBeWrite) {
        const beRead = beReads.get(roomTypeId);
        const room = (grid?.rooms || []).find(
          (item) => Number(item?.pms_room_type_id) === Number(roomTypeId)
        );

        for (const date of dateKeys) {
          const existingRow = beRead?.rowsByDate?.[date] || {};
          const hasAvailability = Object.prototype.hasOwnProperty.call(
            globalUpdates,
            "availability"
          );

          let targetAvailability = hasAvailability
            ? Math.max(0, Number(globalUpdates.availability || 0))
            : existingRow?.allotment;

          // Global rule-only updates must not invent Booking Engine inventory.
          if (
            globalKeys.length &&
            !hasAvailability &&
            (targetAvailability === null ||
              targetAvailability === undefined ||
              targetAvailability === "")
          ) {
            throw new Error(
              `Booking Engine availability is not initialized for room ${roomTypeId} on ${date}. Include Availability in this bulk update first.`
            );
          }

          // Direct-price-only updates can safely preserve/seed the BE calendar
          // from the current global availability or physical room total.
          if (
            targetAvailability === null ||
            targetAvailability === undefined ||
            targetAvailability === ""
          ) {
            const presence = room?.availability_source?.[date];
            const cmAvailability = Number(room?.availability?.[date]);

            targetAvailability =
              presence === "stored" ||
              presence === "channel_manager" ||
              (Number.isFinite(cmAvailability) && cmAvailability > 0)
                ? Math.max(0, cmAvailability)
                : Math.max(0, Number(room?.total_rooms || 0));
          }

          const bePatch = {};
          if (
            Object.prototype.hasOwnProperty.call(
              globalUpdates,
              "stop_sell"
            )
          ) {
            bePatch.stopSell = Boolean(globalUpdates.stop_sell);
          }
          if (
            Object.prototype.hasOwnProperty.call(
              globalUpdates,
              "min_stay"
            )
          ) {
            bePatch.minStay = Math.max(
              1,
              Number(globalUpdates.min_stay || 1)
            );
          }
          if (
            Object.prototype.hasOwnProperty.call(
              globalUpdates,
              "closed_to_arrival"
            )
          ) {
            bePatch.cta = Boolean(globalUpdates.closed_to_arrival);
          }
          if (
            Object.prototype.hasOwnProperty.call(
              globalUpdates,
              "closed_to_departure"
            )
          ) {
            bePatch.ctd = Boolean(globalUpdates.closed_to_departure);
          }

          const pricingPatch = {};
          if (
            hasDirectPricing &&
            Number(roomTypeId) === Number(normalizedRoomIds[0])
          ) {
            if (hasDirectBaseRate) {
              pricingPatch.overrideRate = Number(directPricing.baseRate);
            }
            if (directRateOverrides.length) {
              pricingPatch.rateOverrides = directRateOverrides.map((rate) => ({
                ...rate,
                rate_id: Number(rate?.rate_id),
                currency_code: String(
                  rate?.currency_code || currency || "INR"
                ).toUpperCase(),
              }));
            }
          }

          beItems.push(
            buildPreservedBeCalendarItem({
              roomTypeId,
              date,
              availability: Number(targetAvailability),
              currency,
              beRead,
              restrictionPatch: bePatch,
              pricingPatch,
            })
          );
        }
      }

      const completedTargets = [];
      const recoveryTasks = [];
      let downstreamBlocked = false;

      const globalPayload = globalKeys.length
        ? {
            property_id: propertyId,
            start_date: dayjs(bulkStart).format("YYYY-MM-DD"),
            end_date: dayjs(bulkEnd).format("YYYY-MM-DD"),
            days_of_week: weekdays,
            room_type_ids: normalizedRoomIds,
            updates: globalUpdates,
            push_to_channex: true,
            currency,
          }
        : null;

      const otaRates = [];
      if (hasOtaPricing) {
        for (const date of dateKeys) {
          for (const rate of otaRateTemplates) {
            const amount = Number(rate?.amount);
            if (!rate?.rate_plan_id || !Number.isFinite(amount) || amount < 0) {
              continue;
            }
            otaRates.push({
              rate_plan_id: String(rate.rate_plan_id),
              occupancy: Math.max(1, Number(rate?.occupancy) || 1),
              date,
              amount,
            });
          }
        }
      }

      const otaPayload = otaRates.length
        ? {
            property_id: propertyId,
            currency,
            push_to_channex: true,
            changes: {
              availability: [],
              rates: otaRates,
              restrictions: [],
            },
          }
        : null;

      if (globalPayload) {
        try {
          await channelsApi.ariBulkUpdate(globalPayload);
          completedTargets.push("Channel Manager / Channex global controls");
        } catch (globalError) {
          console.error("Channel Manager bulk global write failed.", globalError);
          recoveryTasks.push({
            id: `cm-bulk-${dateKeys[0]}-${dateKeys[dateKeys.length - 1]}`,
            target: "Channel Manager / Channex global controls",
            kind: "ariBulk",
            payload: globalPayload,
          });
          downstreamBlocked = true;
        }
      }

      if (otaPayload) {
        if (downstreamBlocked) {
          recoveryTasks.push({
            id: `ota-bulk-${dateKeys[0]}-${dateKeys[dateKeys.length - 1]}`,
            target: "OTA / Channex pricing",
            kind: "ariSave",
            payload: otaPayload,
          });
        } else {
          try {
            await channelsApi.ariSave(otaPayload);
            completedTargets.push("OTA / Channex pricing");
          } catch (otaError) {
            console.error("OTA bulk pricing write failed.", otaError);
            recoveryTasks.push({
              id: `ota-bulk-${dateKeys[0]}-${dateKeys[dateKeys.length - 1]}`,
              target: "OTA / Channex pricing",
              kind: "ariSave",
              payload: otaPayload,
            });
          }
        }
      }

      if (beItems.length) {
        if (downstreamBlocked) {
          recoveryTasks.push({
            id: `be-bulk-${dateKeys[0]}-${dateKeys[dateKeys.length - 1]}`,
            target: "Booking Engine",
            kind: "beSave",
            payload: { items: beItems },
          });
        } else {
          try {
            await channelsApi.bookingEngineSaveManualCalendar({ items: beItems });
            completedTargets.push("Booking Engine");
          } catch (beWriteError) {
            console.error("Booking Engine bulk commercial write failed.", beWriteError);
            recoveryTasks.push({
              id: `be-bulk-${dateKeys[0]}-${dateKeys[dateKeys.length - 1]}`,
              target: "Booking Engine",
              kind: "beSave",
              payload: { items: beItems },
            });
          }
        }
      }

      if (recoveryTasks.length) {
        const partial = completedTargets.length > 0;
        setDistributionHealth({
          status: partial ? "partial" : "error",
          message: partial
            ? `${completedTargets.join(", ")} saved. ${recoveryTasks.length} distribution step${recoveryTasks.length === 1 ? " needs" : "s need"} recovery.`
            : `Bulk distribution did not complete. ${recoveryTasks.length} step${recoveryTasks.length === 1 ? " is" : "s are"} ready to retry safely.`,
          updatedAt: dayjs().format("HH:mm"),
        });
        registerDistributionRecovery({
          operation,
          label: `Complete bulk distribution for ${dateKeys.length} date(s)`,
          succeededTargets: completedTargets,
          tasks: recoveryTasks,
        });
        message.warning(
          partial
            ? "Bulk update partially completed. Retry only the failed distribution step(s) from Distribution Trust."
            : "Bulk update could not complete. Distribution Trust has preserved the pending steps for retry."
        );
        await loadGrid();
        return { success: false, partial };
      }

      setPreviewChanges({});
      setDistributionHealth({
        status: "synced",
        message:
          hasDirectPricing && hasOtaPricing
            ? `Direct + OTA pricing saved for ${dateKeys.length} date(s) from one commercial action.`
            : globalKeys.length && hasDirectPricing
              ? `Global controls + Direct pricing saved for ${dateKeys.length} date(s).`
              : hasDirectPricing
                ? `Booking Engine Direct pricing saved for ${dateKeys.length} date(s).`
                : hasOtaPricing
                  ? `OTA pricing saved for ${dateKeys.length} date(s).`
                  : `Selling status saved across ${dateKeys.length} date(s).`,
        updatedAt: dayjs().format("HH:mm"),
      });
      setDistributionRecovery(null);

      message.success(
        hasDirectPricing && hasOtaPricing
          ? "Direct + OTA pricing saved."
          : globalKeys.length && hasDirectPricing
            ? "Global controls and Direct pricing saved."
            : hasDirectPricing
              ? "Booking Engine Direct pricing saved."
              : hasOtaPricing
                ? "OTA pricing saved."
                : "Selling status saved everywhere."
      );
      await loadGrid();
      return { success: true };
    } catch (error) {
      console.error("Bulk commercial update failed.", error);
      setDistributionHealth({
        status: "error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Bulk commercial update failed.",
        updatedAt: dayjs().format("HH:mm"),
      });
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Bulk commercial update failed."
      );
      return { success: false };
    } finally {
      setDistributionSavingKey(null);
    }
  };


  const retryDistributionRecovery = useCallback(async () => {
    const snapshot = distributionRecovery;
    if (!snapshot?.tasks?.length || distributionRetrying) return;

    setDistributionRetrying(true);
    setDistributionHealth({
      status: "saving",
      message: `Retrying ${snapshot.tasks.length} failed distribution step${snapshot.tasks.length === 1 ? "" : "s"}…`,
      updatedAt: null,
    });

    const remaining = [];
    const recoveredTargets = [];

    for (const task of snapshot.tasks) {
      try {
        if (task.kind === "beSave") {
          await channelsApi.bookingEngineSaveManualCalendar(task.payload);
        } else if (task.kind === "directPricing") {
          const result = await saveDayDirectPricing({
            ...task.payload,
            quiet: true,
            skipReload: true,
          });
          if (!result?.success) throw result?.error || new Error("Direct pricing retry failed.");
        } else if (task.kind === "otaPricing") {
          const result = await saveDayOtaPricing({
            ...task.payload,
            quiet: true,
            skipReload: true,
          });
          if (!result?.success) throw result?.error || new Error("OTA pricing retry failed.");
        } else if (task.kind === "globalSelling") {
          const result = await saveDaySellingStatusEverywhere(task.payload);
          if (!result?.success) throw result?.error || new Error("Selling-status retry failed.");
        } else if (task.kind === "ariSave") {
          await channelsApi.ariSave(task.payload);
        } else if (task.kind === "ariBulk") {
          await channelsApi.ariBulkUpdate(task.payload);
        } else {
          throw new Error(`Unsupported recovery task: ${task.kind}`);
        }
        recoveredTargets.push(task.target || "Distribution target");
      } catch (error) {
        console.error("Distribution recovery task failed.", task, error);
        remaining.push(task);
      }
    }

    if (!remaining.length) {
      setDistributionRecovery(null);
      setDistributionHealth({
        status: "synced",
        message: `Recovery complete. ${recoveredTargets.join(", ")} ${recoveredTargets.length === 1 ? "is" : "are"} back in sync.`,
        updatedAt: dayjs().format("HH:mm"),
      });
      setDistributionOperation((current) =>
        current
          ? { ...current, recoveredAt: dayjs().format("DD MMM YYYY · HH:mm:ss") }
          : current
      );
      message.success("Failed distribution step recovered successfully.");
      await loadGrid();
    } else {
      setDistributionRecovery({
        ...snapshot,
        succeededTargets: [
          ...new Set([
            ...(snapshot.succeededTargets || []),
            ...recoveredTargets,
          ]),
        ],
        tasks: remaining,
      });
      setDistributionHealth({
        status: "partial",
        message: `${remaining.length} distribution step${remaining.length === 1 ? " still needs" : "s still need"} attention.`,
        updatedAt: dayjs().format("HH:mm"),
      });
      message.warning("Some distribution recovery steps still need attention.");
    }

    setDistributionRetrying(false);
  }, [
    distributionRecovery,
    distributionRetrying,
    loadGrid,
  ]);

  const verifyVisibleRangeWithChannex = useCallback(async () => {
    if (!propertyId || !calendarRoomId) {
      message.error("Select a room type before verifying with Channex.");
      return;
    }

    const verifyStart = safeYMD(startDate);
    const verifyEnd = dayjs(startDate).add(days - 1, "day").format("YYYY-MM-DD");

    setChannexVerifyLoading(true);
    try {
      const response = await channelsApi.verifyChannexAri({
        property_id: propertyId,
        start_date: verifyStart,
        end_date: verifyEnd,
        room_type_id: calendarRoomId,
      });

      const result = response?.data || {};
      setChannexVerifyResult(result);
      setChannexVerifyOpen(true);

      const mismatchCount = Number(result?.summary?.mismatch_checks || 0);
      if (mismatchCount === 0) {
        setDistributionHealth({
          status: "synced",
          message: `Verified with Channex · ${result?.summary?.total_checks || 0} checks matched.`,
          updatedAt: dayjs().format("HH:mm"),
        });
        message.success("NetShine and Channex match for the selected room and visible range.");
      } else {
        setDistributionHealth({
          status: "partial",
          message: `${mismatchCount} Channex readback mismatch(es) need attention.`,
          updatedAt: dayjs().format("HH:mm"),
        });
        message.warning(`${mismatchCount} Channex mismatch(es) found. Review the verification result.`);
      }
    } catch (error) {
      console.error("Channex verification failed.", error);
      setDistributionHealth({
        status: "error",
        message: error?.response?.data?.message || "Channex verification failed.",
        updatedAt: dayjs().format("HH:mm"),
      });
      message.error(error?.response?.data?.message || "Unable to verify against Channex.");
    } finally {
      setChannexVerifyLoading(false);
    }
  }, [propertyId, calendarRoomId, startDate, days]);

  const clearFilters = () => {
    setRoomFilter(null);
    setRateFilter(null);
  };

  const trustStatus = distributionRecovery?.tasks?.length
    ? "partial"
    : mappingHealth?.status === "error"
      ? "error"
      : mappingHealth?.unmappedCount > 0
        ? "partial"
        : distributionHealth?.status || "idle";

  const trustLabel =
    trustStatus === "synced"
      ? "Distribution healthy"
      : trustStatus === "saving"
        ? "Distribution syncing"
        : trustStatus === "partial"
          ? "Distribution needs attention"
          : trustStatus === "error"
            ? "Distribution issue"
            : "Distribution ready";

  if (loading && !grid) {
    return (
      <div className="page-wrapper-full">
        <div className="content container-fluid" style={{ paddingTop: 24 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        {/* CHANNEL MANAGER HERO */}
        <section className="cm-channel-hero">
          <span className="cm-channel-hero-orb cm-channel-hero-orb-one" aria-hidden="true" />
          <span className="cm-channel-hero-orb cm-channel-hero-orb-two" aria-hidden="true" />

          <div className="cm-channel-hero-main">
            <div className="cm-channel-hero-copy">
              <div className="cm-channel-hero-eyebrow">
                CHANNEL MANAGER · DISTRIBUTION CONTROL
              </div>

              <div className="cm-channel-hero-title-row">
                <h3>Rates &amp; Availability</h3>
                <span
                  className={[
                    "cm-channel-mode-pill",
                    workspaceView === "calendar" ? "is-live" : "is-advanced",
                  ].join(" ")}
                >
                  <i aria-hidden="true" />
                  {workspaceView === "calendar"
                    ? "LIVE DISTRIBUTION"
                    : "ADVANCED ARI"}
                </span>
              </div>

              <p>
                Manage one inventory, selling rules and commercial pricing across
                Booking Engine and connected OTA channels from one workspace.
              </p>

              <div className="cm-channel-hero-meta">
                <span className="cm-channel-meta-property">
                  {subTitleLabel}
                </span>
                <span>{currency}</span>
                <span>{rangeLabel}</span>

                {workspaceView === "calendar" &&
                  distributionHealth?.status !== "idle" && (
                    <Tooltip title={distributionHealth?.message}>
                      <span
                        className={[
                          "cm-channel-sync-chip",
                          `is-${distributionHealth?.status || "idle"}`,
                        ].join(" ")}
                      >
                        {distributionHealth?.status === "synced"
                          ? `Synced${
                              distributionHealth?.updatedAt
                                ? ` · ${distributionHealth.updatedAt}`
                                : ""
                            }`
                          : distributionHealth?.status === "saving"
                            ? "Saving…"
                            : distributionHealth?.status === "partial"
                              ? "Sync attention"
                              : "Save issue"}
                      </span>
                    </Tooltip>
                  )}

                {workspaceView === "advanced" && isDirty && (
                  <span className="cm-channel-unsaved-chip">
                    Unsaved changes
                  </span>
                )}
              </div>
            </div>

            <div className="cm-channel-hero-mode">
              <div className="cm-channel-hero-mode-label">WORKSPACE</div>
              <div
                className="dist-page-tabs cm-channel-hero-tabs"
                role="tablist"
                aria-label="Rates and availability workspace"
              >
                <button
                  type="button"
                  className={workspaceView === "calendar" ? "active" : ""}
                  onClick={() => setWorkspaceView("calendar")}
                >
                  Calendar
                </button>
                <button
                  type="button"
                  className={workspaceView === "advanced" ? "active" : ""}
                  onClick={() => setWorkspaceView("advanced")}
                >
                  Advanced ARI
                </button>
              </div>
              <small>
                {workspaceView === "calendar"
                  ? "Everyday distribution control"
                  : "Revenue & channel specialist view"}
              </small>
            </div>
          </div>

          <div className="cm-channel-commandbar">
            <div className="cm-channel-command-main">
              {workspaceView === "advanced" ? (
                <div className="cm-toolbar-filter-group cm-channel-filter-group">
                  <Select
                    value={viewMode}
                    onChange={setViewMode}
                    style={{ width: 174 }}
                    options={VIEW_OPTIONS}
                    popupClassName="cm-select-popup"
                    getPopupContainer={() => document.body}
                  />
                  <Select
                    allowClear
                    value={roomFilter}
                    onChange={setRoomFilter}
                    placeholder="Rooms"
                    style={{ width: 160 }}
                    options={roomsForFilters}
                    popupClassName="cm-select-popup"
                    getPopupContainer={() => document.body}
                  />
                  <Select
                    allowClear
                    value={rateFilter}
                    onChange={setRateFilter}
                    placeholder="Rates"
                    style={{ width: 180 }}
                    options={ratesForFilters}
                    popupClassName="cm-select-popup"
                    getPopupContainer={() => document.body}
                  />
                  <Button
                    className="cm-modern-btn cm-toolbar-btn cm-clear-btn"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                </div>
              ) : (
                <div className="cm-channel-room-control">
                  <span>Room type</span>
                  <Select
                    value={calendarRoomId}
                    onChange={setCalendarRoomId}
                    placeholder="Room type"
                    style={{ width: 230 }}
                    options={roomsForFilters}
                    popupClassName="cm-select-popup"
                    getPopupContainer={() => document.body}
                  />
                </div>
              )}

              <div className="cm-channel-range-control">
                <span>Period</span>
                <Select
                  value={days}
                  onChange={setDays}
                  style={{ width: 112 }}
                  options={DAYS_OPTIONS}
                  popupClassName="cm-select-popup"
                  getPopupContainer={() => document.body}
                />
              </div>

              <div className="cm-datepicker-anchor" aria-hidden="true">
                <DatePicker
                  ref={datePickerRef}
                  selected={startDate.toDate()}
                  onChange={(d) => d && setStartDate(dayjs(d).startOf("day"))}
                  dateFormat="yyyy-MM-dd"
                  className="reactDateInput"
                  popperClassName="cm-datepicker-popper"
                  calendarClassName="cm-datepicker-calendar"
                  popperPlacement="bottom-end"
                />
              </div>
            </div>

            <div className="cm-channel-command-actions">
              <Button
                className="cm-modern-btn cm-toolbar-btn"
                onClick={goToday}
              >
                Today
              </Button>

              <Button
                className="cm-modern-btn cm-toolbar-btn"
                icon={<ReloadOutlined />}
                loading={gridLoading}
                onClick={loadGrid}
              >
                Refresh
              </Button>

              {workspaceView === "advanced" ? (
                <>
                  <Tooltip
                    title={
                      !isDirty ? "Make changes in the grid to enable saving" : ""
                    }
                  >
                    <Button
                      className="cm-modern-btn cm-primary-action"
                      type="primary"
                      icon={<SaveOutlined />}
                      disabled={!isDirty}
                      onClick={() => setReviewOpen(true)}
                    >
                      Review &amp; Save
                    </Button>
                  </Tooltip>

                  <Button
                    className="cm-modern-btn cm-toolbar-btn"
                    disabled={!isDirty}
                    onClick={confirmReset}
                  >
                    Reset
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    className="cm-modern-btn cm-toolbar-btn cm-desktop-secondary"
                    loading={channexVerifyLoading}
                    onClick={verifyVisibleRangeWithChannex}
                  >
                    Verify
                  </Button>

                  <Button
                    className="cm-modern-btn cm-primary-action"
                    type="primary"
                    onClick={() => setPreviewBulkOpen(true)}
                  >
                    Bulk Update
                  </Button>
                </>
              )}

              <Dropdown
                menu={{
                  items: actionItems,
                  onClick: handleActionsMenuClick,
                }}
                trigger={["click"]}
                overlayStyle={{ zIndex: 6600 }}
                getPopupContainer={() => document.body}
              >
                <Button className="cm-modern-btn cm-toolbar-btn">
                  More <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          </div>

          {workspaceView === "calendar" && (
            <div className={`dist-trust-strip is-${trustStatus}`}>
              <button
                type="button"
                className="dist-trust-strip-main"
                onClick={() => setTrustCenterOpen(true)}
              >
                <span className="dist-trust-strip-shield">✓</span>
                <span className="dist-trust-strip-copy">
                  <strong>{trustLabel}</strong>
                  <small>
                    {mappingHealth.totalRooms
                      ? `${mappingHealth.mappedCount}/${mappingHealth.totalRooms} Direct room mappings`
                      : "Room mapping check pending"}
                    {distributionRecovery?.tasks?.length
                      ? ` · ${distributionRecovery.tasks.length} retry pending`
                      : channexVerifyResult?.summary?.total_checks
                        ? ` · ${Number(channexVerifyResult?.summary?.mismatch_checks || 0)} Channex mismatch(es)`
                        : " · Channex verification available"}
                  </small>
                </span>
              </button>
              <div className="dist-trust-strip-actions">
                {distributionRecovery?.tasks?.length ? (
                  <Button
                    size="small"
                    type="primary"
                    loading={distributionRetrying}
                    onClick={retryDistributionRecovery}
                  >
                    Retry failed step{distributionRecovery.tasks.length === 1 ? "" : "s"}
                  </Button>
                ) : null}
                <Button size="small" onClick={() => setTrustCenterOpen(true)}>
                  Trust Center
                </Button>
              </div>
            </div>
          )}

          {workspaceView === "calendar" && (
            <div className="cm-channel-read-state">
              <Tooltip
                title={
                  calendarBeReadState?.message ||
                  "OTA inventory/rates use the existing Advanced ARI read. Direct pricing uses existing Booking Engine APIs."
                }
              >
                <span
                  className={[
                    "cm-channel-read-chip",
                    `is-${calendarBeReadState?.status || "idle"}`,
                  ].join(" ")}
                >
                  <i aria-hidden="true" />
                  {calendarBeReadState?.status === "live"
                    ? "Direct + OTA live"
                    : calendarBeReadState?.status === "loading"
                      ? "Loading Direct pricing"
                      : calendarBeReadState?.status === "unmapped"
                        ? "Direct mapping check"
                        : calendarBeReadState?.status === "error"
                          ? "Direct pricing issue"
                          : "OTA live · Direct pending"}
                </span>
              </Tooltip>
            </div>
          )}

          {workspaceView === "advanced" && isDirty && (
            <div className="cm-channel-dirty-strip">
              <div>
                <strong>Unsaved ARI changes</strong>
                <span>Review the staged OTA changes before publishing.</span>
              </div>
              <Space>
                <Button
                  className="cm-modern-btn cm-toolbar-btn"
                  size="small"
                  onClick={() => setReviewOpen(true)}
                >
                  Review
                </Button>
                <Button
                  className="cm-modern-btn cm-toolbar-btn"
                  size="small"
                  danger
                  onClick={confirmReset}
                >
                  Discard
                </Button>
              </Space>
            </div>
          )}
        </section>

        {/* WORKSPACE */}
        <Card className="cm-workspace-card" style={{ borderRadius: 16 }}>
          {gridLoading && !grid ? (
            <Spin />
          ) : grid?.rooms?.length ? (
            workspaceView === "calendar" ? (
              <DistributionCalendarPreview
                grid={calendarData}
                roomId={calendarRoomId}
                beRateOptions={calendarBeRates}
                beReadState={calendarBeReadState}
                previewChanges={previewChanges}
                distributionHealth={distributionHealth}
                currency={currency}
                startDateLabel={dayjs(startDate).format("DD MMM YYYY")}
                onPrev={goPrev}
                onNext={goNext}
                onPickStartDate={() => datePickerRef.current?.setOpen?.(true)}
                onOpenDay={setPreviewDay}
              />
            ) : (
              <div className="cm-advanced-ari-shell">
                <div className="cm-advanced-ari-intro">
                  <div>
                    <div className="cm-advanced-kicker">Advanced ARI</div>
                    <strong>OTA rate & availability matrix</strong>
                    <span>Specialist Channex workspace. Booking Engine plans stay out of this grid.</span>
                  </div>
                  <div className="cm-advanced-intro-actions">
                    <label className="cm-direct-ref-toggle">
                      <Switch
                        size="small"
                        checked={showAdvancedDirectReference}
                        onChange={setShowAdvancedDirectReference}
                      />
                      <span>Direct reference</span>
                    </label>
                    <div className="cm-advanced-legend" aria-label="Advanced ARI legend">
                      <span><i className="avl" />Availability</span>
                      <span><i className="rate" />OTA rate</span>
                      <span><i className="edited" />Edited</span>
                    </div>
                  </div>
                </div>

                {showAdvancedDirectReference && (
                  <div className="cm-direct-reference-panel">
                    {!roomFilter ? (
                      <div className="cm-direct-reference-empty">
                        Select one room in the Rooms filter to show its consolidated Direct price.
                      </div>
                    ) : calendarBeReadState?.status === "loading" ? (
                      <div className="cm-direct-reference-empty">Loading Direct reference…</div>
                    ) : calendarBeReadState?.status !== "live" ? (
                      <div className="cm-direct-reference-empty">
                        {calendarBeReadState?.message || "Direct reference is unavailable for this room."}
                      </div>
                    ) : (
                      <>
                        <div className="cm-direct-reference-head">
                          <div>
                            <strong>Direct reference · {advancedDirectRoom?.name || "Selected room"}</strong>
                            <span>Read only · consolidated effective Booking Engine rate</span>
                          </div>
                          <Tag>Reference only</Tag>
                        </div>
                        <div className="cm-direct-reference-days">
                          {advancedDirectReference.map(({ date, summary }) => (
                            <div key={date} className="cm-direct-reference-day">
                              <span>{dayjs(date).format("DD MMM")}</span>
                              <strong>
                                {summary
                                  ? `${currency === "INR" ? "₹" : currency === "USD" ? "$" : currency === "GBP" ? "£" : ""}${Number(summary.amount).toLocaleString("en-IN")}`
                                  : "—"}
                              </strong>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
                <AriGrid
                  grid={grid}
                  draft={draft}
                  setDraft={setDraft}
                  viewMode={viewMode}
                  roomFilter={roomFilter}
                  rateFilter={rateFilter}
                  startDateLabel={dayjs(startDate).format("DD MMM YYYY")}
                  onPrev={goPrev}
                  onNext={goNext}
                  onPickStartDate={() => datePickerRef.current?.setOpen?.(true)}
                  onEditRatePlan={openEditRatePlan}
                />
              </div>
            )
          ) : (
            <div style={{ padding: 24 }}>
              <Text type="secondary">No rooms/rate plans found. Complete provisioning + mapping first.</Text>
            </div>
          )}
        </Card>

        <DistributionDayPreviewDrawer
          open={!!previewDay}
          onClose={() => setPreviewDay(null)}
          room={previewDay?.room || null}
          date={previewDay?.date || null}
          beRateOptions={calendarBeRates}
          beReadState={calendarBeReadState}
          currency={currency}
          initialPreview={activePreviewKey ? previewChanges[activePreviewKey] : null}
          savingAvailability={distributionSavingKey === `day|${Number(previewDay?.room?.pms_room_type_id || 0)}|${previewDay?.date || ""}`}
          savingDirectRate={
            distributionSavingKey === `direct|${Number(previewDay?.room?.pms_room_type_id || 0)}|${previewDay?.date || ""}` ||
            distributionSavingKey === `pricing|${Number(previewDay?.room?.pms_room_type_id || 0)}|${previewDay?.date || ""}`
          }
          savingOtaRate={
            distributionSavingKey === `ota|${Number(previewDay?.room?.pms_room_type_id || 0)}|${previewDay?.date || ""}` ||
            distributionSavingKey === `pricing|${Number(previewDay?.room?.pms_room_type_id || 0)}|${previewDay?.date || ""}`
          }
          distributionStatus={distributionHealth}
          onSaveSellingStatus={saveDaySellingStatusEverywhere}
          onSaveDirectPricing={saveDayDirectPricing}
          onSaveOtaPricing={saveDayOtaPricing}
          onSaveCommercialPricing={saveDayCommercialPricing}
        />

        <DistributionBulkPreviewDrawer
          open={previewBulkOpen}
          onClose={() => setPreviewBulkOpen(false)}
          rooms={grid?.rooms || []}
          currency={currency}
          savingAvailability={distributionSavingKey === "bulk"}
          onLoadDirectPricing={loadBulkDirectPricing}
          onSaveCommercialUpdate={saveBulkCommercialUpdate}
        />

        <Modal
          open={channexVerifyOpen}
          onCancel={() => setChannexVerifyOpen(false)}
          footer={[
            <Button
              className="cm-modern-btn cm-primary-action"
              type="primary"
              key="close"
              onClick={() => setChannexVerifyOpen(false)}
            >
              Done
            </Button>,
          ]}
          width={760}
          title="Channex Verification"
          zIndex={6800}
          centered
          className="cm-modern-modal cm-channex-verify-modal"
        >
          <div className="dist-channex-verify">
            <div className="dist-channex-verify-summary">
              <div>
                <span>Status</span>
                <strong className={Number(channexVerifyResult?.summary?.mismatch_checks || 0) === 0 ? "ok" : "warn"}>
                  {Number(channexVerifyResult?.summary?.mismatch_checks || 0) === 0 ? "Matched" : "Needs attention"}
                </strong>
              </div>
              <div>
                <span>Checks</span>
                <strong>{Number(channexVerifyResult?.summary?.total_checks || 0)}</strong>
              </div>
              <div>
                <span>Mismatches</span>
                <strong>{Number(channexVerifyResult?.summary?.mismatch_checks || 0)}</strong>
              </div>
              <div>
                <span>Channex Min Stay</span>
                <strong>{String(channexVerifyResult?.channex_property?.min_stay_type || "—")}</strong>
              </div>
            </div>

            <div className="dist-channex-verify-note">
              This is an explicit diagnostic readback. The Calendar continues to use NetShine's local ARI state as the operational source of truth.
            </div>

            {Array.isArray(channexVerifyResult?.mismatches) && channexVerifyResult.mismatches.length ? (
              <div className="dist-channex-mismatch-list">
                {channexVerifyResult.mismatches.slice(0, 30).map((item, index) => (
                  <div key={`${item.domain}-${item.date}-${item.field}-${item.rate_plan_id || ""}-${index}`}>
                    <div>
                      <strong>{item.room_name || `Room ${item.pms_room_type_id}`}</strong>
                      <span>{item.date} · {item.field}{item.rate_plan_id ? ` · rate plan ${String(item.rate_plan_id).slice(0, 8)}…` : ""}</span>
                    </div>
                    <div className="dist-channex-values">
                      <span>NetShine <b>{String(item.expected)}</b></span>
                      <span>Channex <b>{item.actual === null || item.actual === undefined ? "missing" : String(item.actual)}</b></span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="dist-channex-all-good">
                All checked availability and selling restrictions match Channex for this room and date range.
              </div>
            )}

            <div className="dist-channex-cert-audit">
              <strong>Certification checks</strong>
              <span>Separate availability / restrictions: ✓</span>
              <span>Property min-stay mode respected: ✓</span>
              <span>Payload-size batching: ✓</span>
              <span>
                Booking ACK pending: {Number(channexVerifyResult?.certification_audit?.booking_revision_ack_pending || 0)}
              </span>
              <span>
                Booking → PMS availability repush: audit pending
              </span>
            </div>
          </div>
        </Modal>

        <DistributionTrustCenter
          open={trustCenterOpen}
          onClose={() => setTrustCenterOpen(false)}
          health={{ ...distributionHealth, status: trustStatus }}
          operation={distributionOperation}
          mapping={mappingHealth}
          verification={channexVerifyResult}
          recovery={distributionRecovery}
          retrying={distributionRetrying}
          verifying={channexVerifyLoading}
          onRetry={retryDistributionRecovery}
          onVerify={verifyVisibleRangeWithChannex}
          onOpenMapping={openChannelMapping}
          channelLabel={channelLabel}
        />

        {/* Existing live Bulk Update remains untouched for Advanced ARI */}
        <BulkUpdateDrawer
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          propertyId={propertyId}
          rooms={grid?.rooms || []}
          currency={currency}
          api={channelsApi}
          onApplied={loadGrid}
        />
<RatePlanDrawer
  open={ratePlanOpen}
  mode={ratePlanMode}
  propertyId={propertyId}
  rooms={grid?.rooms || []}
  currency={currency}
  initialData={editingRatePlan}
  api={channelsApi}
  onClose={closeRatePlanDrawer}
  onSaved={handleRatePlanSaved}
/>
        {/* Review & Save */}
        <ChangesReviewDrawer
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          draft={draft}
          grid={grid}
          currency={currency}
          onConfirmSave={saveChanges}
        />
      </div>

      <style>{`
        .reactDateInput{
          border: none !important;
          outline: none !important;
          font-weight: 800;
          color: #0f172a;
          width: 135px;
          background: transparent;
        }
        .reactDateInput::placeholder{ color: rgba(15,23,42,0.45); }
      `}</style>
    </div>
  );
}
